import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Routes, Route, Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import SectionFooter from './SectionFooter';
import HealthDisclaimer from './HealthDisclaimer';
import landingData from '../data/health/health-landing';
import principlesData from '../data/health/health-principles';
import sugarData from '../data/health/health-sugar';
import ageBrackets, { ageBracketReferences } from '../data/health/age-brackets';
import foodTopics from '../data/health/food-topics';
import './Health.css';

// ─── Shared Components ───────────────────────────────────────────────

function HealthNav() {
  const location = useLocation();
  const path = location.pathname;

  const links = [
    { to: '/health', label: 'Overview' },
    { to: '/health/principles', label: 'Principles' },
    { to: '/health/sugar', label: 'Sugar' },
    { to: '/health/by-age', label: 'By Age' },
    { to: '/health/food', label: 'Food & Nutrients' },
  ];

  const isActive = (to) => {
    if (to === '/health') return path === '/health';
    return path.startsWith(to);
  };

  return (
    <nav className="health-nav">
      {links.map(({ to, label }) => (
        <Link key={to} to={to} className={isActive(to) ? 'active' : ''}>
          {label}
        </Link>
      ))}
    </nav>
  );
}

function References({ references }) {
  if (!references || references.length === 0) return null;
  return (
    <div className="health-references">
      <h3>References</h3>
      <ol>
        {references.map((ref) => (
          <li key={ref.id} id={`ref-${ref.id}`}>
            {ref.url ? (
              <a href={ref.url} target="_blank" rel="noopener noreferrer">{ref.text}</a>
            ) : (
              ref.text
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function CitationLink({ id }) {
  return (
    <a className="health-citation" href={`#ref-${id}`} title={`See reference ${id}`}>
      [{id}]
    </a>
  );
}

// Render text with inline citation markers like [1], [2]
function TextWithCitations({ text }) {
  if (!text) return null;
  const parts = text.split(/(\[\d+\])/g);
  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/^\[(\d+)\]$/);
        if (match) {
          return <CitationLink key={i} id={parseInt(match[1], 10)} />;
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

// Render content arrays (strings and { type: "bullets", items: [] } objects)
function ContentRenderer({ content }) {
  if (!content) return null;
  return (
    <>
      {content.map((item, i) => {
        if (typeof item === 'string') {
          return <p key={i}><TextWithCitations text={item} /></p>;
        }
        if (item.type === 'bullets') {
          return (
            <ul key={i}>
              {item.items.map((bullet, j) => (
                <li key={j}><TextWithCitations text={bullet} /></li>
              ))}
            </ul>
          );
        }
        return null;
      })}
    </>
  );
}

// ─── Landing Page ────────────────────────────────────────────────────

function HealthLanding() {
  return (
    <div className="health-landing">
      <h1>{landingData.title}</h1>
      <p className="health-subtitle">{landingData.subtitle}</p>

      <div className="health-intro">
        {landingData.intro.map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>

      <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: '32px', marginBottom: '12px' }}>
        How to use this section
      </h2>
      <div className="health-section-cards">
        {landingData.howToUse.map((item, i) => (
          <Link key={i} to={item.path} className="health-section-card">
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </Link>
        ))}
        <Link to={landingData.sugarSection.path} className="health-section-card">
          <h3>{landingData.sugarSection.title}</h3>
          <p>{landingData.sugarSection.description}</p>
        </Link>
      </div>

      <h2 style={{ fontSize: '16px', fontWeight: 600, marginTop: '32px', marginBottom: '12px' }}>
        Core philosophy
      </h2>
      <div className="health-philosophy">
        <ul>
          {landingData.philosophy.map((item, i) => (
            <li key={i}>
              {item.link ? (
                <Link to={item.link}>{item.text}</Link>
              ) : (
                item.text
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Principles Page ─────────────────────────────────────────────────

function HealthPrinciples() {
  return (
    <div>
      <div className="health-page-header">
        <h1>{principlesData.title}</h1>
        <p>{principlesData.subtitle}</p>
      </div>

      {principlesData.sections.map((section) => (
        <div key={section.id} className="health-content-section">
          <h2>{section.title}</h2>
          <ContentRenderer content={section.content} />
        </div>
      ))}

      {principlesData.dosAndDonts && (
        <div className="health-dos-donts">
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid var(--border-light)' }}>
            Do's and Don'ts
          </h2>
          {principlesData.dosAndDonts.map((group) => (
            <div key={group.category}>
              <h3>{group.category}</h3>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '50%' }}>Do</th>
                    <th style={{ width: '50%' }}>Avoid / Limit</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((row, i) => (
                    <tr key={i}>
                      <td className="do-cell">{row.do}</td>
                      <td className="dont-cell">{row.dont}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <References references={principlesData.references} />
    </div>
  );
}

// ─── Sugar Deep Dive ─────────────────────────────────────────────────

function HealthSugar() {
  return (
    <div>
      <div className="health-page-header">
        <h1>{sugarData.title}</h1>
        <p>{sugarData.subtitle}</p>
      </div>

      {sugarData.sections.map((section) => (
        <div key={section.id} className="health-content-section">
          <h2>{section.title}</h2>
          <ContentRenderer content={section.content} />
        </div>
      ))}

      <div className="health-content-section">
        <h2>Sugar Impact by Age</h2>
        <div className="health-sugar-age-callouts">
          {sugarData.ageBracketNotes.map((note) => (
            <div key={note.slug} className="health-sugar-age-callout">
              <strong>
                <Link to={`/health/by-age/${note.slug}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  {note.bracket}
                </Link>
              </strong>
              <p><TextWithCitations text={note.note} /></p>
            </div>
          ))}
        </div>
      </div>

      {sugarData.practical && (
        <div className="health-content-section">
          <h2>{sugarData.practical.title}</h2>

          <h3>Hidden names for sugar on labels</h3>
          <ul>
            {sugarData.practical.hiddenSugarNames.map((name, i) => (
              <li key={i}>{name}</li>
            ))}
          </ul>

          <h3>Daily limits (guidelines)</h3>
          <ul>
            {sugarData.practical.dailyLimits.map((item, i) => (
              <li key={i}>
                <strong>{item.group}:</strong>{' '}
                <TextWithCitations text={item.limit} />
              </li>
            ))}
          </ul>

          <h3>Practical swaps</h3>
          <table className="health-dos-donts" style={{ marginTop: '8px' }}>
            <thead>
              <tr>
                <th>Instead of</th>
                <th>Try</th>
              </tr>
            </thead>
            <tbody>
              {sugarData.practical.swaps.map((swap, i) => (
                <tr key={i}>
                  <td className="dont-cell">{swap.from}</td>
                  <td className="do-cell">{swap.to}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <References references={sugarData.references} />
    </div>
  );
}

// ─── By Age Index ────────────────────────────────────────────────────

function HealthByAgeIndex() {
  return (
    <div>
      <div className="health-page-header">
        <h1>Health by Age</h1>
        <p>What matters most at each stage of life</p>
      </div>

      <div className="health-age-grid">
        {ageBrackets.map((bracket) => (
          <Link key={bracket.slug} to={`/health/by-age/${bracket.slug}`} className="health-age-card">
            <span className="age-label">{bracket.label}</span>
            <span className="age-summary">{bracket.summary}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── By Age Detail ───────────────────────────────────────────────────

function HealthByAgePage() {
  const { slug } = useParams();
  const bracket = ageBrackets.find((b) => b.slug === slug);

  if (!bracket) {
    return (
      <div>
        <Link to="/health/by-age" className="health-back">&#8592; All age brackets</Link>
        <p>Age bracket not found.</p>
      </div>
    );
  }

  const currentIndex = ageBrackets.findIndex((b) => b.slug === slug);
  const prev = currentIndex > 0 ? ageBrackets[currentIndex - 1] : null;
  const next = currentIndex < ageBrackets.length - 1 ? ageBrackets[currentIndex + 1] : null;

  return (
    <div>
      <Link to="/health/by-age" className="health-back">&#8592; All age brackets</Link>

      <div className="health-page-header">
        <h1>{bracket.label}</h1>
        <p>{bracket.summary}</p>
      </div>

      <div className="health-bracket-section">
        <h2>Nutrition Priorities</h2>
        <ul>
          {bracket.nutritionPriorities.map((item, i) => (
            <li key={i}><TextWithCitations text={item} /></li>
          ))}
        </ul>
      </div>

      <div className="health-bracket-section">
        <h2>Movement & Fitness</h2>
        <ul>
          {bracket.movementPriorities.map((item, i) => (
            <li key={i}><TextWithCitations text={item} /></li>
          ))}
        </ul>
      </div>

      <div className="health-bracket-section">
        <h2>What to Avoid / Watch Out For</h2>
        <ul>
          {bracket.avoidWatchOut.map((item, i) => (
            <li key={i}><TextWithCitations text={item} /></li>
          ))}
        </ul>
        <p style={{ fontSize: '13px', marginTop: '8px' }}>
          <Link to="/health/sugar" style={{ color: 'var(--link-color)' }}>
            Read the full sugar deep dive →
          </Link>
        </p>
      </div>

      <div className="health-bracket-section">
        <h2>Sleep & Recovery</h2>
        <p><TextWithCitations text={bracket.sleepRecovery} /></p>
      </div>

      <div className="health-bracket-section">
        <h2>Common Mistakes</h2>
        <ul>
          {bracket.commonMistakes.map((item, i) => (
            <li key={i}><TextWithCitations text={item} /></li>
          ))}
        </ul>
      </div>

      <div className="health-bracket-section">
        <h2>Sets Up for Later</h2>
        <p><TextWithCitations text={bracket.setsUpForLater} /></p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px', paddingTop: '16px', borderTop: '1px solid var(--border-light)' }}>
        {prev ? (
          <Link to={`/health/by-age/${prev.slug}`} style={{ color: 'var(--link-color)', textDecoration: 'none', fontSize: '13px' }}>
            ← {prev.label}
          </Link>
        ) : <span />}
        {next ? (
          <Link to={`/health/by-age/${next.slug}`} style={{ color: 'var(--link-color)', textDecoration: 'none', fontSize: '13px' }}>
            {next.label} →
          </Link>
        ) : <span />}
      </div>

      <References references={ageBracketReferences} />
    </div>
  );
}

// ─── Food Search Index ───────────────────────────────────────────────

function HealthFoodIndex() {
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase().trim();
    return foodTopics.filter((topic) => {
      if (topic.title.toLowerCase().includes(q)) return true;
      if (topic.slug.includes(q)) return true;
      if (topic.synonyms.some((s) => s.toLowerCase().includes(q))) return true;
      return false;
    }).slice(0, 10);
  }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0 && filtered[highlightedIndex]) {
      navigate(`/health/food/${filtered[highlightedIndex].slug}`);
      setShowSuggestions(false);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [query]);

  return (
    <div>
      <div className="health-page-header">
        <h1>Food & Nutrient Search</h1>
        <p>Search for any nutrient, food topic, or health concern</p>
      </div>

      <div className="health-food-search">
        <input
          ref={inputRef}
          type="text"
          className="health-search-input"
          placeholder="Search — try protein, vitamin C, belly fat, sleep..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onKeyDown={handleKeyDown}
        />
        {showSuggestions && query.trim() && (
          <div className="health-search-suggestions">
            {filtered.length > 0 ? (
              filtered.map((topic, i) => (
                <button
                  key={topic.slug}
                  className={`health-search-suggestion ${i === highlightedIndex ? 'highlighted' : ''}`}
                  onMouseDown={() => navigate(`/health/food/${topic.slug}`)}
                >
                  {topic.title}
                </button>
              ))
            ) : (
              <div className="health-no-results">No topics found for "{query}"</div>
            )}
          </div>
        )}
      </div>

      <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Browse all topics
      </h3>
      <div className="health-topic-chips">
        {foodTopics.map((topic) => (
          <Link key={topic.slug} to={`/health/food/${topic.slug}`} className="health-topic-chip">
            {topic.title}
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── Food Topic Detail ───────────────────────────────────────────────

function HealthFoodTopic() {
  const { topic: topicSlug } = useParams();
  const topic = foodTopics.find((t) => t.slug === topicSlug);

  if (!topic) {
    return (
      <div>
        <Link to="/health/food" className="health-back">&#8592; All topics</Link>
        <p>Topic not found.</p>
      </div>
    );
  }

  const relatedItems = topic.relatedTopics
    ? topic.relatedTopics
        .map((slug) => foodTopics.find((t) => t.slug === slug))
        .filter(Boolean)
    : [];

  return (
    <div>
      <Link to="/health/food" className="health-back">&#8592; All topics</Link>

      <div className="health-topic-detail">
        <h1>{topic.title}</h1>

        <div className="health-topic-section">
          <h2>What it is</h2>
          <p>{topic.whatItIs}</p>
        </div>

        <div className="health-topic-section">
          <h2>Why it matters</h2>
          <p><TextWithCitations text={topic.whyItMatters} /></p>
        </div>

        <div className="health-topic-section">
          <h2>Good sources</h2>
          <ul>
            {topic.goodSources.map((source, i) => (
              <li key={i}>{source}</li>
            ))}
          </ul>
        </div>

        <div className="health-topic-section">
          <h2>How much you need</h2>
          <p><TextWithCitations text={topic.howMuch} /></p>
        </div>

        {topic.deficiencyExcess && (
          <div className="health-topic-section">
            <h2>Signs of deficiency / excess</h2>
            <p><TextWithCitations text={topic.deficiencyExcess} /></p>
          </div>
        )}

        {topic.ageNotes && (
          <div className="health-topic-section">
            <h2>Age-specific notes</h2>
            <p><TextWithCitations text={topic.ageNotes} /></p>
          </div>
        )}

        {topic.myths && topic.myths.length > 0 && (
          <div className="health-topic-section">
            <h2>Common myths</h2>
            {topic.myths.map((myth, i) => (
              <div key={i} className="health-myth">
                <div className="myth-label">Myth</div>
                <p className="myth-text">"{myth.myth}"</p>
                <p className="reality-text">
                  <strong>Reality: </strong>
                  <TextWithCitations text={myth.reality} />
                </p>
              </div>
            ))}
          </div>
        )}

        {relatedItems.length > 0 && (
          <div className="health-related">
            <h3>Related topics</h3>
            <div className="health-topic-chips">
              {relatedItems.map((related) => (
                <Link key={related.slug} to={`/health/food/${related.slug}`} className="health-topic-chip">
                  {related.title}
                </Link>
              ))}
            </div>
          </div>
        )}

        <References references={topic.references} />
      </div>
    </div>
  );
}

// ─── Main Health Component ───────────────────────────────────────────

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Health() {
  return (
    <div className="health-container">
      <ScrollToTop />
      <div className="health-theme-toggle">
        <ThemeToggle />
      </div>
      <HealthNav />
      <Routes>
        <Route path="/" element={<HealthLanding />} />
        <Route path="/principles" element={<HealthPrinciples />} />
        <Route path="/sugar" element={<HealthSugar />} />
        <Route path="/by-age" element={<HealthByAgeIndex />} />
        <Route path="/by-age/:slug" element={<HealthByAgePage />} />
        <Route path="/food" element={<HealthFoodIndex />} />
        <Route path="/food/:topic" element={<HealthFoodTopic />} />
      </Routes>
      <HealthDisclaimer />
      <SectionFooter />
    </div>
  );
}

export default Health;
